import { api } from '@/helpers/apiClient'
import logger, { toErrorMessage } from '@/helpers/logger'
import {
  treeProblemTypeDtoSchema,
  treeProblemTypeListDtoSchema,
  type TreeProblemTypeDto,
  type TreeProblemTypeListDto,
} from '@lib/dto/tree-problem-type.dto'
import type {
  CreateProblemSolutionInput,
  UpdateProblemSolutionInput,
} from '@lib/input/problem-solution.input'
import type {
  CreateTreeProblemTypeInput,
  UpdateTreeProblemTypeInput,
} from '@lib/input/tree-problem-type.input'
import type { ITreeProblemTypeService } from '@/types/TreeProblemTypeService.types'

/** The problem directory. Responses validated against the shared schemas. */
class TreeProblemTypeService implements ITreeProblemTypeService {
  async list(): Promise<TreeProblemTypeListDto> {
    try {
      return await api.get('/tree-problem-types', treeProblemTypeListDtoSchema)
    } catch (error) {
      logger.error('Failed to load tree problems', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async create(
    input: CreateTreeProblemTypeInput,
  ): Promise<TreeProblemTypeDto> {
    try {
      return await api.post(
        '/tree-problem-types',
        input,
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to create a tree problem', {
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async update(
    id: string,
    input: UpdateTreeProblemTypeInput,
  ): Promise<TreeProblemTypeDto> {
    try {
      return await api.put(
        `/tree-problem-types/${encodeURIComponent(id)}`,
        input,
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update a tree problem', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setArchived(
    id: string,
    archived: boolean,
  ): Promise<TreeProblemTypeDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/tree-problem-types/${encodeURIComponent(id)}/${action}`,
        {},
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} a tree problem`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
  /*
   * Every solution call returns the whole PROBLEM TYPE, because that is what
   * the screen renders — the solutions are nested inside it, so handing back
   * the part would only make the caller fetch the whole thing again.
   */

  async createSolution(
    problemTypeId: string,
    input: CreateProblemSolutionInput,
  ): Promise<TreeProblemTypeDto> {
    try {
      return await api.post(
        `/tree-problem-types/${encodeURIComponent(problemTypeId)}/solutions`,
        input,
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to add a solution', {
        problemTypeId,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async updateSolution(
    id: string,
    input: UpdateProblemSolutionInput,
  ): Promise<TreeProblemTypeDto> {
    try {
      return await api.put(
        `/tree-problem-types/solutions/${encodeURIComponent(id)}`,
        input,
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error('Failed to update a solution', {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }

  async setSolutionArchived(
    id: string,
    archived: boolean,
  ): Promise<TreeProblemTypeDto> {
    const action = archived ? 'archive' : 'restore'
    try {
      return await api.post(
        `/tree-problem-types/solutions/${encodeURIComponent(id)}/${action}`,
        {},
        treeProblemTypeDtoSchema,
      )
    } catch (error) {
      logger.error(`Failed to ${action} a solution`, {
        id,
        error: toErrorMessage(error),
      })
      throw error
    }
  }
}

export const treeProblemTypeService = new TreeProblemTypeService()
